import { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SidebarProvider } from '@/components/ui/sidebar';
import { AppSidebar } from './AppSidebar';
import { SecondarySidebar } from './SecondarySidebar';
import { UserMenu } from './UserMenu';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { Footer } from './Footer';
import { AnnouncementBanner } from '@/components/announcements/AnnouncementBanner';
import { useTicketNotifications } from '@/hooks/useTicketNotifications';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { Button } from '@/components/ui/button';
import { OfferIcon } from '@/config/icons';
import { cn } from '@/lib/utils';

interface AppLayoutProps {
  children: ReactNode;
  showSecondarySidebar?: boolean;
}

export function AppLayout({ children, showSecondarySidebar = false }: AppLayoutProps) {
  // Enable real-time ticket notifications
  useTicketNotifications();
  
  // Update document title with unread count
  useDocumentTitle();
  
  const location = useLocation();
  const isOfferActive = location.pathname === '/offer';

  return (
    <SidebarProvider defaultOpen={true}>
      <div className="h-screen flex flex-col w-full overflow-hidden">
        <div className="flex flex-1 min-h-0">
          <AppSidebar />
          
          <div className="flex-1 flex flex-col min-w-0">
            {/* Header */}
            <header className="h-14 border-b border-border bg-background flex items-center px-4 gap-4 shrink-0">
              <div className="flex-1" />
              <Button
                variant="ghost"
                size="sm"
                asChild
                className={cn(
                  "gap-2",
                  isOfferActive && "bg-accent text-accent-foreground"
                )}
              >
                <Link to="/offer">
                  <OfferIcon className="h-4 w-4" />
                  <span>Offre</span>
                </Link>
              </Button>
              <WorkspaceSwitcher variant="topbar" />
              <UserMenu />
            </header>
            
            {/* Content area with optional secondary sidebar */}
            <div className="flex-1 flex min-h-0 overflow-hidden">
              {showSecondarySidebar && <SecondarySidebar />}
              
              {/* Main content */}
              <main className="flex-1 min-h-0 overflow-hidden flex flex-col">
                <div className="p-4 shrink-0">
                  <AnnouncementBanner />
                </div>
                <div className="-mt-4 flex-1 min-h-0 overflow-auto flex flex-col">
                  {children}
                </div>
              </main>
            </div>
          </div>
        </div>

        {/* Footer - pleine largeur */}
        <div className="shrink-0">
          <Footer />
        </div>
      </div>
    </SidebarProvider>
  );
}
